import type { LocationQuery } from 'vue-router'
import { queryParamsToPreserve } from '#content/middleware/preserveQuery.global'

/**
 * Checks if the current route's query matches a given query string.
 *
 * @remarks
 * This utility function deeply compares the active `vue-router` query object against a
 * target URL query string (`data`). It intelligently handles complex, nested filter objects
 * and ignores transient CMS parameters.
 *
 *
 * It performs two types of comparisons:
 * 1.  **Filter Comparison**: If `data` contains a `filters` key, it parses the JSON string
 * from both `data` and the route `query`, then deeply compares the resulting objects.
 * 2.  **General Comparison**: For all other parameters, it directly compares the route's
 * query object with the parsed and cleaned `data` object.
 *
 * @param data - The URL query string to compare against (e.g., `category=shoes&sort=price`).
 * @param query - The current `LocationQuery` object from the `vue-router` route.
 * @returns `true` if the significant parts of the queries match, otherwise `false`.
 */
export default (data: string, query: LocationQuery): boolean => {
  const { filters, ...queryObj } = Object.fromEntries(new URLSearchParams(data).entries())

  queryParamsToPreserve.forEach((k) => delete queryObj[k])

  const results: boolean[] = []

  if (filters !== undefined) {
    // PLP filters require special handling, since we need to check nested stringified object
    if (!query.filters) return false
    if (filters === '') return true
    try {
      const filtersObj = JSON.parse(filters)
      const routeFiltersObj = JSON.parse(query.filters.toString())
      results.push(matchDeep(routeFiltersObj, filtersObj))
    }
    catch {
      results.push(false)
    }
  }

  if (Object.keys(queryObj).length) {
    results.push(matchDeep(query, queryObj))
  }

  return results.every(Boolean)
}
